import { validationConstants } from "constants/formValidation";
import { orderStatusConstants } from "constants/order";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { StyledEnabledToggle } from "pages/CreateOrder/CreateOrder.styled";
import { getOrderStatus } from "pages/CreateOrder/utils/helpers";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputTextarea } from "primereact/inputtextarea";
import { useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext, FieldValues } from "react-hook-form";
import styled from "styled-components";
import { TourAnchor } from "training/anchors";
import { IAccount } from "types/Account/Account";
import { IOrder } from "types/Order/Order";

import { StepCard } from "../StepCard";
import { StepKey } from "../StepCard/helper";
import { useStepStatus } from "../StepCard/useStepStatus";
import {
  redoOrderSMSTemplate,
  redoOrderNoBalanceDueSMSTemplate,
  createOrderSMSTemplate,
} from "./helpers";

export interface ICommunicationFields {
  textMessage: string;
}

const StyledSmsPreview = styled.div`
  border: 1px solid var(--brand-gray-200);
  border-radius: 12px;
  overflow: hidden;
  background: var(--brand-gray-50);

  .sms-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    padding: 0.875rem 1.25rem;
    border-bottom: 1px solid var(--brand-gray-200);

    font-size: 0.9375rem;
    font-weight: var(--font-weight-semibold);
    color: var(--brand-black);
  }

  .sms-body {
    padding: 1.25rem;
  }

  .sms-bubble {
    display: inline-block;
    max-width: 80%;
    padding: 0.875rem 1.125rem;
    border-radius: 16px;

    background: color-mix(
      in srgb,
      var(--brand-purple-700) 8%,
      var(--brand-white)
    );
    color: var(--brand-purple-700);

    font-size: 0.9375rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

export function Communication({
  account,
  redo,
  draft,
  order,
}: {
  account: IAccount;
  redo?: boolean;
  draft?: boolean;
  order?: IOrder;
}) {
  const {
    control,
    trigger,
    formState: { errors, dirtyFields },
    setValue,
    watch,
    getValues,
  } = useFormContext<FieldValues>();
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const status = useStepStatus(StepKey.COMMUNICATION);

  const orderTypefield = watch("orderType");
  const outreachEnabled = watch("outreachEnabled");
  const customText = watch("customText");
  const preferredContactMethod = watch("preferredContactMethod");
  const orderTypeField = watch("orderType");
  const amountField = watch("amount");
  const totalAmountField = watch("totalAmount");
  const scheduledCommunicationDate = watch("scheduledFirstCommunicationDate");
  const scheduledFirstCommunicationSent = watch(
    "scheduledFirstCommunicationSent"
  );
  const scheduledCommunicationCustomText = watch(
    "scheduledCommunicationCustomText"
  );

  // UI-only flag, shared through the form so Submit / SaveDraft can lock their
  // actions while the message is open for editing. mapFormToRequestBody builds
  // its payload field by field, so this never reaches the API.
  const isEditing = !!watch("isEditingMessage");

  // What the message was before this edit began, so Cancel can put it back.
  const messageSnapshot = useRef<{
    customText: string;
    sendDefaultText: boolean;
  } | null>(null);

  const startEditing = () => {
    messageSnapshot.current = {
      customText: getValues("customText"),
      sendDefaultText: !!getValues("sendDefaultText"),
    };
    setValue("isEditingMessage", true);
  };

  const saveMessage = () => {
    messageSnapshot.current = null;
    setValue("isEditingMessage", false);
  };

  const cancelEditing = () => {
    if (messageSnapshot.current) {
      setValue("customText", messageSnapshot.current.customText);
      setValue("sendDefaultText", messageSnapshot.current.sendDefaultText);
      messageSnapshot.current = null;
    }
    setValue("isEditingMessage", false);
  };

  // The message the patient would get if we sent nothing of our own. Editing
  // away from it is what makes the message "custom".
  const defaultMessage = useMemo(() => {
    const orderStatus = getOrderStatus({
      amount: amountField,
      totalAmount: totalAmountField,
      orderType: orderTypeField,
    });

    if (redo) {
      return orderStatus === orderStatusConstants.NO_BALANCE_DUE
        ? redoOrderNoBalanceDueSMSTemplate
        : redoOrderSMSTemplate;
    }

    if (orderStatus === orderStatusConstants.NO_BALANCE_DUE) {
      return createOrderSMSTemplate;
    }

    return (
      account?.communicationTemplates?.find(
        (template) => template.orderType === orderTypefield
      )?.smsTemplate ?? ""
    );
  }, [
    account,
    orderTypefield,
    orderTypeField,
    amountField,
    totalAmountField,
    redo,
  ]);

  // Only ever relaxes back to the default when the text matches it exactly;
  // anything else sends the message the user is looking at.
  const handleMessageChange = (
    value: string,
    onChange: (next: string) => void
  ) => {
    onChange(value);
    setValue("sendDefaultText", value.trim() === (defaultMessage ?? "").trim());
  };

  useEffect(() => {
    const orderStatus = getOrderStatus({
      amount: amountField,
      totalAmount: totalAmountField,
      orderType: orderTypeField,
    });
    const communicationTemplatesForOrderType =
      account?.communicationTemplates?.find((template) => {
        return template.orderType === orderTypefield;
      });
    const textMessage =
      orderStatus === orderStatusConstants.NO_BALANCE_DUE
        ? createOrderSMSTemplate
        : communicationTemplatesForOrderType?.smsTemplate;
    const userHasEdited = !!dirtyFields?.customText;

    if (redo) {
      const messageTemaple =
        orderStatus === orderStatusConstants.NO_BALANCE_DUE
          ? redoOrderNoBalanceDueSMSTemplate
          : redoOrderSMSTemplate;
      let customMessage = messageTemaple;
      if (scheduledCommunicationDate && !scheduledFirstCommunicationSent) {
        customMessage =
          customText || scheduledCommunicationCustomText || textMessage;
      }
      if (
        order?.scheduledFirstCommunicationDate &&
        !scheduledFirstCommunicationSent &&
        !scheduledCommunicationDate
      ) {
        customMessage = textMessage || "";
      }
      setValue("customText", customMessage);
      return;
    }
    if (draft) {
      if (scheduledCommunicationDate && !scheduledFirstCommunicationSent) {
        setValue(
          "customText",
          customText || scheduledCommunicationCustomText || textMessage
        );
        return;
      }
      if (
        order?.scheduledFirstCommunicationDate &&
        !scheduledFirstCommunicationSent &&
        !scheduledCommunicationDate
      ) {
        setValue("customText", textMessage);
        return;
      }
    }
    if (userHasEdited) {
      return;
    }
    setValue("customText", textMessage);
  }, [
    orderTypefield,
    account,
    redo,
    amountField,
    totalAmountField,
    orderTypeField,
    scheduledCommunicationDate,
    scheduledFirstCommunicationSent,
    scheduledCommunicationCustomText,
    draft,
    order,
    dirtyFields.customText,
  ]);

  useEffect(() => {
    if (preferredContactMethod === "Email") {
      setValue("sendDefaultText", true);
    }
  }, [preferredContactMethod]);

  // The editor can disappear mid-edit (outreach switched off, contact method
  // changed to Email). Leaving the flag set would disable Create / Save Draft
  // with nothing on screen to explain why.
  const editorVisible =
    !!outreachEnabled &&
    !!preferredContactMethod &&
    preferredContactMethod !== "Email";

  useEffect(() => {
    if (!editorVisible) {
      setValue("isEditingMessage", false);
    }
  }, [editorVisible]);

  if (!preferredContactMethod || preferredContactMethod === "Email") {
    return null;
  }
  return (
    <StepCard
      status={status}
      icon={<i className="pi pi-comment text-base" />}
      title="Patient Communication"
      description={
        outreachEnabled
          ? "Optionally preview or customize what the patient will receive"
          : "Patient communication is disabled"
      }
      data-testid="communication"
      data-tour={TourAnchor.PatientCommunication}
      action={
        <Controller
          control={control}
          name="outreachEnabled"
          render={({ field: { onChange, value } }) => (
            <StyledEnabledToggle
              type="button"
              id="outreachEnabled"
              data-testid="outreachEnabled"
              $enabled={!!value}
              aria-pressed={!!value}
              onClick={() => onChange(!value)}
            >
              <span className="dot" />
              {value ? "Enabled" : "Disabled"}
            </StyledEnabledToggle>
          )}
        />
      }
    >
      <div className={outreachEnabled ? "" : "hidden"}>
        <StyledSmsPreview>
          <div className="sms-header">
            <i className="pi pi-mobile" />
            SMS preview
          </div>

          <div className="sms-body">
            <Controller
              name="customText"
              control={control}
              rules={{
                // Hidden while communication is off, so it cannot be required —
                // the user would have no way to see or fix the error.
                required: outreachEnabled
                  ? validationConstants.TEXT_MESSAGE_FORM.CUSTOM_TEXT
                  : false,
              }}
              render={({ field: { onChange, onBlur, value } }) => {
                if (!isEditing) {
                  return <div className="sms-bubble">{value}</div>;
                }

                return (
                  <>
                    <InputTextarea
                      id="customText"
                      autoComplete="off"
                      className="w-full"
                      value={value || ""}
                      rows={3}
                      aria-labelledby="customText"
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: `customText`,
                          trigger,
                        });
                      }}
                      onChange={(e) =>
                        handleMessageChange(e.target.value, onChange)
                      }
                    />

                    {getFormErrorMessage("customText", errors)}
                  </>
                );
              }}
            />
          </div>
        </StyledSmsPreview>

        <div className="flex flex-wrap align-items-center gap-2 mt-3">
          {isEditing ? (
            <>
              <Button
                type="button"
                data-testid="saveMessage"
                className="p-button-action white-space-nowrap"
                icon="pi pi-check"
                label="Save message"
                onClick={saveMessage}
              />
              <Button
                type="button"
                data-testid="cancelMessage"
                className="p-button-action p-button-neutral white-space-nowrap"
                icon="pi pi-times"
                label="Cancel"
                onClick={cancelEditing}
              />
            </>
          ) : (
            <Button
              type="button"
              data-testid="editMessage"
              className="p-button-action p-button-neutral white-space-nowrap"
              icon="pi pi-pencil"
              label="Edit message"
              onClick={startEditing}
            />
          )}
        </div>
      </div>
    </StepCard>
  );
}
